import {
  HttpErrorResponse,
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { environment } from '@hub-env/environment';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { AuthRedirectInitiatedError } from './auth-redirect-initiated-error';
import { AuthService } from './auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private injector: Injector) {}

  private _authService: AuthService | null = null;

  // Lazy-injected to avoid a constructor-time cycle between AuthInterceptor
  // (registered with HTTP_INTERCEPTORS) and AuthService.
  get authService(): AuthService {
    return (this._authService ??= this.injector.get(AuthService));
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Absolute-prefix match on a compile-time origin. A stale origin here silently
    // skips authentication; see environment.k8s.ts.
    if (!req.url.startsWith(environment.API_URL)) {
      return next.handle(req);
    }

    // No cached account: nothing to silently acquire against. Force login
    // rather than leak an unauthenticated request.
    if (!this.authService.hasAccounts()) {
      this.authService.triggerReauth('login');
      return throwError(() => new AuthRedirectInitiatedError());
    }

    // AuthService owns retry/classify/redirect; we either get a token or the
    // request errors (redirect already in flight) — never a tokenless send.
    return this.authService.acquireApiTokenOrReauth().pipe(
      switchMap((result) =>
        next.handle(this.withAuthHeaders(req, result.accessToken))
      ),
      catchError((error) => {
        // A 401 here means the server rejected a token MSAL was happy to issue;
        // let AuthService classify it (re-auth vs access-denied).
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.authService.handleApiAuthRejection(error.status);
        }
        return throwError(() => error);
      })
    );
  }

  private withAuthHeaders(
    req: HttpRequest<any>,
    accessToken: string
  ): HttpRequest<any> {
    return req.clone({
      headers: req.headers
        .set('Authorization', `Bearer ${accessToken}`)
        .set('x-api-key', environment.apiConfig.apiKey ?? ''),
    });
  }
}
